import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { PM_SHELL_GLOBAL_SEARCH_HOST, type PmShellGlobalSearchHost } from './pm-shell-global-search-host';
import { type PmGlobalSearchItemApi } from '../../../core/services/api/pm-global-search-api.service';
import { PmDatePipe } from '../../../core/pipes/pm-date.pipe';

/** Global search results popup (under the top bar search box): results grouped by module, keyboard highlight, loading / empty states. */
@Component({
  selector: 'pm-shell-global-search',
  standalone: true,
  imports: [PmDatePipe],
  templateUrl: './pm-shell-global-search.component.html',
  styleUrl: './pm-shell-global-search.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PmShellGlobalSearchComponent {

  /** What this section still reads from the workspace (see pm-shell-global-search-host.ts). */
  private readonly workspace = inject(PM_SHELL_GLOBAL_SEARCH_HOST);

  get headerGlobalSearchActiveIndex() {
    return this.workspace.headerGlobalSearchActiveIndex;
  }

  get headerGlobalSearchDebouncing() {
    return this.workspace.headerGlobalSearchDebouncing;
  }

  get headerGlobalSearchError() {
    return this.workspace.headerGlobalSearchError;
  }

  get headerGlobalSearchFlatResults() {
    return this.workspace.headerGlobalSearchFlatResults;
  }

  get headerGlobalSearchGroupedResults() {
    return this.workspace.headerGlobalSearchGroupedResults;
  }

  get headerGlobalSearchHasSearched() {
    return this.workspace.headerGlobalSearchHasSearched;
  }

  get headerGlobalSearchLoading() {
    return this.workspace.headerGlobalSearchLoading;
  }

  get headerGlobalSearchModalLeftPx() {
    return this.workspace.headerGlobalSearchModalLeftPx;
  }

  get headerGlobalSearchModalWidthPx() {
    return this.workspace.headerGlobalSearchModalWidthPx;
  }

  get headerGlobalSearchQuery() {
    return this.workspace.headerGlobalSearchQuery;
  }

  get headerGlobalSearchResults() {
    return this.workspace.headerGlobalSearchResults;
  }

  isHeaderGlobalSearchModalOpen(...args: Parameters<PmShellGlobalSearchHost['isHeaderGlobalSearchModalOpen']>): ReturnType<PmShellGlobalSearchHost['isHeaderGlobalSearchModalOpen']> {
    return this.workspace.isHeaderGlobalSearchModalOpen(...args);
  }

  get isSearchExpanded() {
    return this.workspace.isSearchExpanded;
  }

  onHeaderGlobalSearchResultClick(...args: Parameters<PmShellGlobalSearchHost['onHeaderGlobalSearchResultClick']>): ReturnType<PmShellGlobalSearchHost['onHeaderGlobalSearchResultClick']> {
    return this.workspace.onHeaderGlobalSearchResultClick(...args);
  }

  searchModuleKey(...args: Parameters<PmShellGlobalSearchHost['searchModuleKey']>): ReturnType<PmShellGlobalSearchHost['searchModuleKey']> {
    return this.workspace.searchModuleKey(...args);
  }

  stopProp(...args: Parameters<PmShellGlobalSearchHost['stopProp']>): ReturnType<PmShellGlobalSearchHost['stopProp']> {
    return this.workspace.stopProp(...args);
  }

  headerGlobalSearchItemTrackId(item: PmGlobalSearchItemApi): string {
    return `${this.searchModuleKey(item)}::${item.id ?? ''}::${item.name ?? ''}`;
  }

  headerGlobalSearchItemModuleLabel(item: PmGlobalSearchItemApi): string {
    const key = this.searchModuleKey(item);
    if (key === 'actions') return 'Action';
    if (key === 'pages') return 'Page';
    if (key === 'clients') return 'Client';
    if (key === 'teams') return 'Team Member';
    if (key === 'tasks') return 'Task';
    if (key === 'notes') return 'Note';
    return 'Result';
  }

  headerGlobalSearchModuleKey(item: PmGlobalSearchItemApi): string {
    return this.searchModuleKey(item);
  }

  headerGlobalSearchItemFlatIndex(item: PmGlobalSearchItemApi): number {
    return this.headerGlobalSearchFlatResults().indexOf(item);
  }
}
